import { useEffect, useState } from 'react';
import PromotionManager from '../../services/api/PromotionManager';
import { GOOGLE_REVIEW_STATUS_TYPES } from '../../services/exports/Constants';
import { GoogleReviewStatusInterface } from '../../services/exports/Interfaces';
import { DataWrappedResponse } from '../../services/exports/Types';
import useToast from '../utility/useToast';
import { useTranslation } from 'react-i18next';
import useInitialData from '../global/useInitialData';
import useAuth from '../global/useAuth';

interface Props {
  autoLoad?: boolean;
}

export default function useGoogleReview({ autoLoad }: Props = {}) {
  const { t } = useTranslation(null, { keyPrefix: 'Hooks:GoogleReview:UseGoogleReview' });

  const { data } = useInitialData();
  const { company } = data;

  const toast = useToast();
  const { isLoggedIn } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const [googleStatusData, setGoogleStatusData] = useState(null);
  const [hasUploadedGoogleReview, setHasUploadedGoogleReview] = useState(false);

  const google_review_program = company?.google_review_program;

  useEffect(() => {
    autoLoad && isLoggedIn && requestGoogleReviewStatus();
    !isLoggedIn && setHasUploadedGoogleReview(false);
  }, [isLoggedIn]);

  useEffect(() => {
    if (googleStatusData) {
      setHasUploadedGoogleReview(googleStatusData?.status !== GOOGLE_REVIEW_STATUS_TYPES.UNDETERMINED);
    }
  }, [googleStatusData]);

  async function requestGoogleReviewStatus(): Promise<DataWrappedResponse<GoogleReviewStatusInterface>> {
    setLoading(true);
    const request = await PromotionManager.getGoogleReviewStatus(company?.id);
    setLoading(false);

    const { response, success } = request;

    if (success) {
      setGoogleStatusData(response?.data?.data);
    }

    return request;
  }

  function throwGoogleReviewUploadedToast(): string | void {
    return toast.success(t('toast_messages.google_review_uploaded', { points: google_review_program?.reward }), {
      duration: Infinity,
    });
  }

  return {
    loading,
    googleStatusData,
    googlePromotion: google_review_program,
    hasGooglePromotion: !!google_review_program,
    hasUploadedGoogleReview,
    requestGoogleReviewStatus,
    throwGoogleReviewUploadedToast,
  };
}
